import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useRejectTaskChecklistItem,
  getGetDailyChecklistQueryKey,
  getGetEventChecklistQueryKey,
} from "@workspace/api-client-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// Phase 42 Plan 15 (TPRF-04) — the validator's reject-with-reason dialog,
// shown beside Validate on any row where `canIValidate` is true and the
// item is awaiting validation (done/N/A'd, not yet validated). The
// validator reviews the proof rendered by 42-14's ProofSummary, then either
// Validates or Rejects here.
//
// Hits 42-11's POST /tasks/checklist-items/:itemId/reject: un-ticks the
// occurrence, clears its proof and the validator's OWN clock (never the
// doer's doerDueAt — that stays historical), and drops the round back to
// in_progress via the awaiting_validation -> in_progress edge 42-02 added.
// Same validator predicate as /validate — doer != validator stays enforced
// server-side; a 403 here means the server disagrees this user may act on
// this item.
//
// Plain markup (local state only, no react-hook-form) — shadcn Form*
// primitives need a <FormField> ancestor and would white-screen this dialog
// otherwise (the standing repo trap, latest 0e61073). Mirrors the shape of
// checklist-daily-tab.tsx's own N/A dialog: a mandatory reason textarea,
// Reject disabled while empty.

function errData(err: unknown): Record<string, unknown> | undefined {
  return (err as { data?: Record<string, unknown> })?.data;
}
function errMessage(err: unknown, fallback: string): string {
  const data = errData(err);
  return (data?.message as string) ?? (data?.error as string) ?? fallback;
}

export function RejectDialog({
  open,
  onOpenChange,
  itemId,
  itemLabel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  itemId: number;
  itemLabel: string;
}) {
  const queryClient = useQueryClient();
  const rejectMutation = useRejectTaskChecklistItem();
  const [reason, setReason] = useState("");

  function close() {
    onOpenChange(false);
    setReason("");
  }

  async function handleSubmit() {
    const trimmed = reason.trim();
    if (trimmed.length === 0 || rejectMutation.isPending) return;
    try {
      await rejectMutation.mutateAsync({ itemId, data: { rejectReason: trimmed } });
      toast.success("Item rejected — sent back to the doer.");
      // Broad prefix invalidation (no params -> no `exact`) so whichever
      // tab is open (Daily/Weekly/Monthly OR Onboarding/Exit) re-fetches
      // immediately — mirrors checklist-event-tab.tsx's own
      // StartProcedureDialog invalidation pattern.
      queryClient.invalidateQueries({ queryKey: getGetDailyChecklistQueryKey() });
      queryClient.invalidateQueries({ queryKey: getGetEventChecklistQueryKey() });
      close();
    } catch (err) {
      // 403 (not this validator's item) / 409 (not currently awaiting
      // validation) — surface the server's own message verbatim, never a
      // generic swallow.
      toast.error(errMessage(err, "Could not reject this item."));
    }
  }

  const remaining = reason.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (rejectMutation.isPending) return;
        onOpenChange(o);
        if (!o) setReason("");
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Reject &amp; send back</DialogTitle>
          <DialogDescription>
            &ldquo;{itemLabel}&rdquo; will un-tick and return to the doer to redo with fresh proof. Explain what needs
            fixing — the doer sees this reason on the row.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5 py-2">
          <label htmlFor="reject-reason" className="text-xs font-medium text-muted-foreground">
            Reason <span className="text-destructive">*</span>
          </label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Photo shows the wrong zone"
            rows={3}
            autoFocus
          />
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={close} disabled={rejectMutation.isPending}>
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleSubmit} disabled={remaining || rejectMutation.isPending}>
            {rejectMutation.isPending ? "Rejecting…" : "Reject"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
